// src/features/dashboard/components/EmployeeLoggedHoursReport/DailyHoursLogTable.tsx

import { DailyLogResponse } from '../../types/Employee/employeeLoggedHours.types';

interface Props {
  data?: DailyLogResponse | null;
}

/* ============================================================
 * FORMAT WORK DATE
 *
 * API:
 * 2026-08-24T00:00:00
 *
 * UI:
 * Mon 24 Aug
 * ============================================================ */

const formatWorkDate = (
  workDate: string,
): string => {
  if (!workDate) {
    return '--';
  }

  const date = new Date(workDate);

  if (Number.isNaN(date.getTime())) {
    return '--';
  }

  const day = date.toLocaleDateString(
    'en-US',
    {
      weekday: 'short',
    },
  );

  const dateNumber = date.toLocaleDateString(
    'en-US',
    {
      day: '2-digit',
    },
  );

  const month = date.toLocaleDateString(
    'en-US',
    {
      month: 'short',
    },
  );

  return `${day} ${dateNumber} ${month}`;
};

/* ============================================================
 * STATUS
 * ============================================================ */

const getStatus = (
  vsTarget: string,
) => {
  if (vsTarget.startsWith('+')) {
    return 'Met';
  }

  if (vsTarget.startsWith('-')) {
    return 'Near';
  }

  return 'Live';
};

/* ============================================================
 * STATUS BADGE
 * ============================================================ */

const getStatusBadge = (
  status: string,
) => {
  switch (status) {
    case 'Met':
      return 'bg-[#198754] text-white';

    case 'Near':
      return 'bg-[#F4B400] text-[#111827]';

    case 'Live':
      return 'bg-[#2563EB] text-white';

    default:
      return 'bg-[#E5E7EB] text-[#111827]';
  }
};

/* ============================================================
 * VS TARGET COLOR
 * ============================================================ */

const getVsTargetColor = (
  vsTarget: string,
) => {
  if (vsTarget.startsWith('+')) {
    return 'text-[#16A34A]';
  }

  if (vsTarget.startsWith('-')) {
    return 'text-[#DC2626]';
  }

  return 'text-[#111827]';
};

/* ============================================================
 * COMPONENT
 * ============================================================ */

const DailyHoursLogTable = ({
  data,
}: Props) => {
  const rows =
    data?.dailyLogs ?? [];

  const summary =
    data?.summary;

  return (
    <div className="rounded-[16px] border border-[#D0D5DD] bg-white p-4">
      {/* ======================================================
          HEADER
      ====================================================== */}

      <div className="mb-3 flex items-center gap-2">
        <h3 className="text-[15px] font-semibold text-[#002B5B]">
          Daily Hours Log · This Week
        </h3>

        <span className="rounded-full bg-[#EEF4FF] px-2 py-[3px] text-[9px] text-[#2563EB]">
          Login, logout, active time and compliance
        </span>
      </div>

      {/* ======================================================
          TABLE
      ====================================================== */}

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-[#D0D5DD]">
              <th className="pb-2 text-left text-[12px] font-semibold">
                Day
              </th>

              <th className="pb-2 text-left text-[12px] font-semibold">
                Login
              </th>

              <th className="pb-2 text-left text-[12px] font-semibold">
                Logout
              </th>

              <th className="pb-2 text-left text-[12px] font-semibold">
                Logged
              </th>

              <th className="pb-2 text-left text-[12px] font-semibold">
                Active
              </th>

              <th className="pb-2 text-left text-[12px] font-semibold">
                Idle
              </th>

              <th className="pb-2 text-left text-[12px] font-semibold">
                vs Target
              </th>

              <th className="pb-2 text-left text-[12px] font-semibold">
                Status
              </th>
            </tr>
          </thead>

          <tbody>
            {rows.length > 0 ? (
              rows.map(
                (row, index) => {
                  const status =
                    getStatus(
                      row.vsTarget,
                    );

                  return (
                    <tr
                      key={`${row.workDate}-${index}`}
                      className="border-b border-[#EAECF0]"
                    >
                      {/* ==================================================
                          DAY

                          Uses workDate instead of dayLabel.

                          2026-08-23 -> Sun 23 Aug
                          2026-08-24 -> Mon 24 Aug
                          2026-08-25 -> Tue 25 Aug
                      ================================================== */}

                      <td className="py-[5px] text-[12px] font-medium whitespace-nowrap">
                        {formatWorkDate(
                          row.workDate,
                        )}
                      </td>

                      {/* Login */}

                      <td className="py-[5px] text-[12px]">
                        {row.login}
                      </td>

                      {/* Logout */}

                      <td className="py-[5px] text-[12px]">
                        {row.logout}
                      </td>

                      {/* Logged */}

                      <td className="py-[5px] text-[12px]">
                        {row.logged}
                      </td>

                      {/* Active */}

                      <td className="py-[5px] text-[12px]">
                        {row.active}
                      </td>

                      {/* Idle */}

                      <td className="py-[5px] text-[12px]">
                        {row.idle}
                      </td>

                      {/* vs Target */}

                      <td
                        className={`py-[5px] text-[12px] ${getVsTargetColor(
                          row.vsTarget,
                        )}`}
                      >
                        {row.vsTarget}
                      </td>

                      {/* Status */}

                      <td className="py-[5px]">
                        <span
                          className={`
                            inline-flex
                            min-w-[38px]
                            items-center
                            justify-center
                            rounded-full
                            px-[6px]
                            py-[1px]
                            text-[9px]
                            font-medium
                            ${getStatusBadge(
                              status,
                            )}
                          `}
                        >
                          {status}
                        </span>
                      </td>
                    </tr>
                  );
                },
              )
            ) : (
              <tr>
                <td
                  colSpan={8}
                  className="py-6 text-center text-[12px] text-[#667085]"
                >
                  No daily hours data available
                </td>
              </tr>
            )}

            {/* ======================================================
                WTD TOTAL
            ====================================================== */}

            <tr className="font-semibold">
              {/* Day */}

              <td className="pt-2 text-[12px]">
                WTD Total
              </td>

              {/* Login */}

              <td />

              {/* Logout */}

              <td />

              {/* Logged */}

              <td className="pt-2 text-[12px]">
                {summary?.totalLogged ??
                  '--'}
              </td>

              {/* Active */}

              <td className="pt-2 text-[12px]">
                {summary?.totalActive ??
                  '--'}
              </td>

              {/* Idle */}

              <td className="pt-2 text-[12px]">
                {summary?.totalIdle ??
                  '--'}
              </td>

              {/* vs Target */}

              <td className="pt-2 text-[12px]">
                {summary?.targetInfo ??
                  '--'}
              </td>

              {/* Percentage */}

              <td className="pt-2 text-[13px] font-bold">
                {summary?.percentageAchieved !=
                  null
                  ? `${summary.percentageAchieved}%`
                  : '--'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DailyHoursLogTable;